import ToastCompact from "@/components/common/toast/ToastCompact";
import { AccountBrand } from "@/screens/analysis/types";
import { IconInfoCircle } from "@tabler/icons-react";
import { toast } from "sonner";

export const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID as string;

// Type declaration for Chrome extension API
declare global {
  interface Window {
    chrome?: {
      runtime?: {
        sendMessage: (
          extensionId: string,
          message: unknown,
          responseCallback?: (response: unknown) => void,
        ) => void;
        lastError?: { message?: string };
      };
    };
  }
}

/**
 * Check if Chrome extension is installed
 * @param extensionId - Optional extension ID, defaults to EXTENSION_ID from env
 */
export const checkExtensionInstalled = (
  extensionId: string = EXTENSION_ID,
): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if we're in a browser environment
    if (
      typeof window === "undefined" ||
      !window.chrome ||
      !window.chrome.runtime ||
      !window.chrome.runtime.sendMessage
    ) {
      resolve(false);
      return;
    }

    try {
      // Try to send a message to the extension
      window.chrome.runtime.sendMessage(extensionId, { action: "ping" }, () => {
        // If we get an error with "Could not establish connection", extension is not installed
        if (window.chrome?.runtime?.lastError) {
          resolve(false);
        } else {
          resolve(true);
        }
      });
    } catch {
      // Fallback: try to check if extension URL is accessible
      try {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = `chrome-extension://${extensionId}/manifest.json`;
      } catch {
        resolve(false);
      }
    }
  });
};

/**
 * Checks if a brand needs to be synced based on last_synced_at timestamp
 * A brand needs sync if it's private and last synced more than 24 hours ago
 */
export const needsSync = (brand: AccountBrand): boolean => {
  if (!brand.is_private) return false;
  if (!brand.last_synced_at) return true;

  const lastSynced = new Date(brand.last_synced_at);
  const now = new Date();
  const hoursSinceSync = (now.getTime() - lastSynced.getTime()) / (1000 * 60 * 60);

  return hoursSinceSync > 24;
};

/**
 * Sends brand sync request to the extension
 * The extension will handle opening tabs and syncing the brands
 * @param brands - Array of AccountBrand objects to sync
 * @param accountId - Account ID to send to extension
 * @param forceSync - If true, sync all brands regardless of needsSync check
 */
export const syncBrandsWithExtension = async (
  brands: AccountBrand[],
  accountId: string,
  forceSync: boolean = false,
): Promise<void> => {
  if (!accountId) {
    console.debug("accountId missing");
    return;
  }

  // Check if extension is installed before attempting to sync
  const isExtensionInstalled = await checkExtensionInstalled();
  if (!isExtensionInstalled) {
    console.debug("Extension not installed");
    return;
  }

  // Filter brands that have ad_library_url
  const brandsToSync = brands.filter(
    (brand) => brand.ad_library_url && (forceSync || needsSync(brand)),
  );

  if (brandsToSync.length === 0) {
    return;
  }

  // Prepare brand details to send to extension
  // Include only the necessary fields for the extension to sync
  const brandDetails = brandsToSync.map((brand) => ({
    id: brand.id,
    page_id: brand.page_id,
    name: brand.name,
    ad_library_url: brand.ad_library_url,
  }));

  try {
    console.debug(
      `Sending sync request to extension for ${brandDetails.length} brand(s)`,
    );
    toast.custom((t) => (
      <ToastCompact
        iconProps={{ size: 16, className: "text-[#2A94F4]" }}
        messageHighlight="Syncing Private brand"
        messageSuffix="in background!"
        hasDescription={true}
        descriptionPrefix="Don't close Ad Library tabs until the sync is done."
        onDismiss={() => toast.dismiss(t)}
        icon={IconInfoCircle}
        iconBackgroundClassName="bg-[#2A94F414]"
      />
    ));
    window.chrome!.runtime!.sendMessage(
      EXTENSION_ID,
      {
        type: "start_external_brand_sync",
        accountId: accountId,
        brands: brandDetails,
      },
      (response) => {
        if (window.chrome?.runtime?.lastError) {
          console.debug(
            "Failed to send sync request to extension:",
            window.chrome.runtime.lastError,
          );
        } else {
          console.debug("Extension sync response:", response);
        }
      },
    );
  } catch (error) {
    console.debug("Error sending sync request to extension:", error);
  }
};
